/**
 * NOTE: I'm doing things a bit hacky here.
 * Normally, controls are rendered with the `controls` array, and
 * that state is owned by Demo. The problem here is that I need to
 * run an effect whenever that state changes, to calculate the
 * actual bounding box sizes. And I want to show that after the
 * component.
 *
 * So, instead, I'm owning the state in this component and rendering
 * the controller manually, outside the `controls` array.
 */

import React from 'react';
import { styled } from '@linaria/react';

import SliderControl from '@/components/Controls/SliderControl';

import FlexDemo from './FlexDemo';
import Output from './Output';
import ShrinkItem from './ShrinkItem';
import useScaledBoxes from './useScaledBoxes';

const FIRST_FLEX_BASIS = 250;
const SECOND_FLEX_BASIS = 250;

// NOTE: `locked` is not actually used, replaced with a screenshot.
// Keeping it around in case I need to generate new screenshots
// (note: will need to play with `align-self` to get the 400px
// indicator to line up.)
function ShrinkIntro({ locked }) {
  const [width, setWidth] = React.useState(locked ? 400 : 600);
  const [flexShrink, setFlexShrink] = React.useState(locked ? 3 : 1);

  const {
    scale,
    firstItemRef,
    secondItemRef,
    firstBBWidth,
    secondBBWidth,
  } = useScaledBoxes({ dependencies: [width, flexShrink] });

  return (
    <FlexDemo
      centered
      initialValues={{
        '--direction': 'row',
      }}
      renderAfter={() => {}}
    >
      {(state) => (
        <>
          <ScaleWrapper
            style={{
              width: 612,
              height: 300 * scale,
              transform: `scale(${scale})`,
            }}
          >
            <Output
              state={state}
              wrapperStyle={{ width: width + 2 * 2 + 4 * 2 }}
            >
              <ShrinkItem
                primary
                ref={firstItemRef}
                actualSize={firstBBWidth}
                flexShrink={flexShrink}
                flexBasis={FIRST_FLEX_BASIS}
                shrunkBy={
                  firstBBWidth
                    ? 100 -
                      Math.round(
                        (firstBBWidth / FIRST_FLEX_BASIS) * 100
                      )
                    : 0
                }
              />
              <ShrinkItem
                ref={secondItemRef}
                actualSize={secondBBWidth}
                flexBasis={SECOND_FLEX_BASIS}
                flexShrink={1}
                shrunkBy={
                  secondBBWidth
                    ? 100 -
                      Math.round(
                        (secondBBWidth / SECOND_FLEX_BASIS) * 100
                      )
                    : 0
                }
              />
            </Output>
          </ScaleWrapper>
          {locked ? (
            <SizeShower>
              <span>400px</span>
            </SizeShower>
          ) : (
            <PseudoControlWrapper>
              <SliderControl
                disabled={locked}
                id="flexShrink"
                label="flex-shrink"
                min={1}
                max={8}
                value={flexShrink}
                setValue={setFlexShrink}
                handleColor="hsl(50deg 100% 60%)"
                handleFocusColor="hsl(50deg 100% 60%)"
                wrapperStyle={{
                  flex: 1,
                  flexBasis: 200,
                  color: 'hsl(50deg 100% 65%)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              />
              <SliderControl
                disabled={locked}
                id="container-width"
                label="Container width"
                min={400}
                max={600}
                value={width}
                setValue={setWidth}
                handleFocusColor="white"
                wrapperStyle={{
                  flex: 1,
                  flexBasis: 200,
                }}
              />
            </PseudoControlWrapper>
          )}
        </>
      )}
    </FlexDemo>
  );
}

const ScaleWrapper = styled.div`
  transform-origin: top center;
`;

const PseudoControlWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 32px;
  width: 100%;
  margin-top: 16px;
`;

const SizeShower = styled.div`
  position: relative;
  width: 412px;
  text-align: center;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: 16px;
    border: 2px solid var(--color-gray-700);
    border-top: none;
    border-radius: 0px 0px 8px 8px;
  }

  span {
    position: relative;
    background: var(--color-background);
    padding: 0px 16px;
  }
`;

export default ShrinkIntro;
